// Bandeja -> Casos (D34, D37): grupos de la persona, filtros de la barra (en la direccion), los casos sin cerrar que
// comparten Mi dia, los avisos, "Siguiente" y el numero del menu, y la columna de vistas + "Abiertos ahora".
import { type QueryClient, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { aCaso, type Caso, type NotaInterna, type RawCaso } from '../../api/casos';
import { gqlTodos } from '../../api/gql';
import { CAMPOS_DETALLE, CAMPOS_LISTA, casosDeVista, contarCasos, contarVistas, vistasCaso } from '../../api/vistasCaso';
import { type DetalleCaso, juntarDetalle } from '../../lib/detalleCaso';
import { useSession } from '../../auth/session';
import { Icon } from '../../lib/icons';
import { gruposVisibles } from '../../lib/gruposPersonales';
import { readJSON, writeJSON } from '../../lib/storage';
import { useMiembros } from '../../data/queries';
import { quitarCaso, usePestanas } from '../../lib/pestanasCaso';
import { type EstadoFiltro, type FiltrosCasos, miDia, type QuienFiltro, senalDe, textoSenal } from '../../lib/senalesCaso';
import { ESTADOS_ABIERTOS, type VistaCaso, vistaVisible } from '../../lib/vistasCaso';
import { type CampoOrden, type DirOrden, escribirPagina, leerPagina, type PaginaCasos, TAM_DEFECTO } from '../../lib/ordenCasos';
import { useBuzonesGrupo } from '../administracion/BuzonesGrupo';

// Refresca listas, conteos y el caso abierto despues de un cambio.
export function refrescarCasos(qc: QueryClient) {
  for (const k of ['casos', 'casos-vista', 'caso', 'vistas-conteo', 'caso-de-hilo']) void qc.invalidateQueries({ queryKey: [k] });
}

export const useVistasCaso = () => useQuery({ queryKey: ['vistas-caso'], queryFn: vistasCaso, staleTime: 5 * 60_000 });

// Una vista que solo mira grupos que ya no existen (se borraron o se desactivaron) no se muestra.
export function useVistasVisibles() {
  const { yo } = useSession();
  const r = useVistasCaso();
  const g = useBuzonesGrupo();
  return useMemo(() => {
    const activos = new Set((g.data ?? []).filter((x) => x.activo).map((x) => x.nombre.toLowerCase()));
    const vigente = (v: { grupos: string[] }) => !g.data || !v.grupos.length || v.grupos.some((n) => activos.has(n.toLowerCase()));
    return (r.data ?? []).filter((v) => vistaVisible(v, yo.miembroId) && vigente(v));
  }, [r.data, g.data, yo.miembroId]);
}

// Grupos que la persona puede ver (el administrador, todos los activos) y de cuales es miembro. Los casos de grupos que
// no existen o estan inactivos en el CRM no se cuentan ni se listan (salvo en "Sin grupo del CRM", solo administrador).
export function useMisGrupos() {
  const { yo, esAdmin } = useSession();
  const g = useBuzonesGrupo();
  const equipo = useMiembros();
  return useMemo(() => {
    // Los grupos de un buzon personal de otra persona no se ven (D62).
    const { nombres, miembro, existentes } = gruposVisibles(g.data ?? [], equipo.data ?? [], { miembroId: yo.miembroId, admin: esAdmin });
    return { nombres, permitidos: nombres, miembro, existentes, cargando: g.isPending || equipo.isPending };
  }, [g.data, g.isPending, equipo.data, equipo.isPending, esAdmin, yo.miembroId]);
}

// Vista elegida: Mi dia (por defecto), Todos, Sin grupo del CRM o una vista guardada (su id).
export type VistaElegida = { tipo: 'dia' } | { tipo: 'todos' } | { tipo: 'huerfanos' } | { tipo: 'guardada'; vista: VistaCaso };

export const VISTA_TODOS: VistaCaso = { id: 'todos', nombre: 'Todos los casos', orden: 0, estados: [], grupos: [], gruposExcluidos: [], etiquetas: [], asignado: '', tituloContiene: '', miembros: [], activa: true };
export const VISTA_SIN_CERRAR: VistaCaso = { ...VISTA_TODOS, id: 'sin-cerrar', estados: ESTADOS_ABIERTOS };

export function useVistaElegida(): VistaElegida {
  const [sp] = useSearchParams();
  const { puede } = useSession();
  const esAdmin = puede('casos.sinGrupo');
  const vistas = useVistasVisibles();
  const v = sp.get('vista') ?? 'dia';
  if (v === 'todos') return { tipo: 'todos' };
  if (v === 'huerfanos' && esAdmin) return { tipo: 'huerfanos' };
  const guardada = vistas.find((x) => x.id === v);
  return guardada ? { tipo: 'guardada', vista: guardada } : { tipo: 'dia' };
}

// Filtros de la barra, en la direccion (?q=&grupo=&estado=&quien=) para compartir el enlace y volver al mismo lugar.
export function useFiltros(): [FiltrosCasos, (cambios: Partial<FiltrosCasos>) => void] {
  const [sp, setSp] = useSearchParams();
  const grupos = useMisGrupos();
  const g = sp.get('grupo') ?? '';
  const e = sp.get('estado') ?? '';
  const qn = sp.get('quien') ?? '';
  const f: FiltrosCasos = {
    q: sp.get('q') ?? '',
    grupo: grupos.nombres.includes(g) ? g : '',
    estado: (['nuevos', 'abiertos', 'pendientes', 'resueltos'].includes(e) ? e : '') as EstadoFiltro,
    quien: (['mios', 'sintomar'].includes(qn) ? qn : '') as QuienFiltro,
  };
  const cambiar = (c: Partial<FiltrosCasos>) => {
    const n = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(c)) {
      if (v) n.set(k, v);
      else n.delete(k);
    }
    // Otros filtros, otra lista: vuelve a la primera pagina.
    n.delete('pag');
    setSp(n, { replace: true });
  };
  return [f, cambiar];
}

// Orden y pagina de la lista (?orden=&dir=&pag=&tam=). El tamano elegido queda ademas en el navegador para la proxima vez.
const CLAVE_TAM = 'consola.casos.porPagina';
export function useOrdenCasos(): [PaginaCasos, (c: Partial<{ campo: CampoOrden; dir: DirOrden; pag: number; tam: number }>) => void] {
  const [sp, setSp] = useSearchParams();
  const p = leerPagina(sp, readJSON<number>(CLAVE_TAM, TAM_DEFECTO));
  const cambiar = (c: Partial<{ campo: CampoOrden; dir: DirOrden; pag: number; tam: number }>) => {
    if (c.tam !== undefined) writeJSON(CLAVE_TAM, c.tam);
    const n = escribirPagina(sp, c);
    // La pagina va al historial (Atras vuelve a la anterior); orden y tamano lo reemplazan.
    setSp(n, { replace: c.pag === undefined });
  };
  return [p, cambiar];
}

// Los casos sin cerrar de los grupos de la persona: una sola consulta para Mi dia, los avisos, "Siguiente" y el menu.
export function useCasosSinCerrar() {
  const grupos = useMisGrupos();
  return useQuery({
    queryKey: ['casos-vista', 'sin-cerrar', grupos.permitidos.join(',')],
    queryFn: async () => (await casosDeVista<RawCaso>(VISTA_SIN_CERRAR, { gruposPermitidos: grupos.permitidos }, 1000)).map(aCaso),
    enabled: !grupos.cargando,
    // Cada 2 min (antes cada minuto): los cambios hechos desde la Consola ya refrescan la lista al momento.
    refetchInterval: 2 * 60_000,
    staleTime: 15_000,
  });
}

// Un caso de la lista abierto en pantalla: la lista no trae el siguiente paso, la respuesta sugerida ni las notas
// internas; se piden aparte (una consulta chica) y se juntan. Mientras no llegan, `cargando` (no se muestra el caso:
// guardar una nota con las notas vacias borraria las anteriores). Si la consulta falla, `caso` queda vacio y quien
// llama lo pide completo.
export function useDetalleCaso(base: Caso | undefined) {
  const r = useQuery({
    queryKey: ['caso', base?.id ?? '', 'detalle'],
    queryFn: async () => (await gqlTodos<DetalleCaso>('casos', CAMPOS_DETALLE, { filtro: { id: { eq: base!.id } }, max: 1 }))[0] ?? null,
    enabled: !!base,
    // Las notas que agrega otra persona aparecen solas mientras el caso esta abierto.
    refetchInterval: 60_000,
  });
  const vistas = useRef<{ id: string; notas: NotaInterna[] }>({ id: '', notas: [] });
  const caso = useMemo(() => {
    if (!base || !r.data) return undefined;
    const c = juntarDetalle(base, r.data, vistas.current.id === base.id ? vistas.current.notas : []);
    vistas.current = { id: base.id, notas: c.notas };
    return c;
  }, [base, r.data]);
  return { caso, cargando: !!base && r.isPending };
}

export function useMiDia(casos: Caso[] | undefined) {
  const { yo } = useSession();
  const grupos = useMisGrupos();
  return useMemo(() => miDia(casos ?? [], yo.miembroId, grupos.miembro), [casos, yo.miembroId, grupos.miembro]);
}

// Numero de "Casos" en el menu: lo que te escribieron, lo que toca perseguir y lo nuevo de tus grupos.
export function useCasosPendientes() {
  const r = useCasosSinCerrar();
  const d = useMiDia(r.data);
  return { data: d.respondieron.length + d.seguimientos.length + d.nuevosMios.length };
}

export function useConteosVistas() {
  const { yo } = useSession();
  const vistas = useVistasVisibles();
  const grupos = useMisGrupos();
  return useQuery({
    queryKey: ['vistas-conteo', vistas.map((v) => v.id).join(','), yo.miembroId, grupos.permitidos.join(',')],
    queryFn: () => contarVistas(vistas, { miembroId: yo.miembroId, gruposPermitidos: grupos.permitidos }),
    enabled: vistas.length > 0 && !grupos.cargando,
    refetchInterval: 2 * 60_000,
    staleTime: 20_000,
  });
}

// Casos sin cerrar cuyo grupo no es un grupo activo del CRM (solo administrador).
export function useConteoHuerfanos() {
  const { puede } = useSession();
  const esAdmin = puede('casos.sinGrupo');
  const grupos = useMisGrupos();
  return useQuery({
    queryKey: ['vistas-conteo', 'huerfanos', grupos.existentes.join(',')],
    queryFn: () =>
      contarCasos({ and: [{ estado: { in: ESTADOS_ABIERTOS } }, { or: [{ not: { grupo: { in: grupos.existentes.length ? grupos.existentes : ['—'] } } }, { grupo: { is: 'NULL' } }] }] }),
    enabled: esAdmin && !grupos.cargando,
    staleTime: 60_000,
  });
}

// Columna de la Bandeja bajo "Casos": Mi dia, Todos, las vistas guardadas y los casos abiertos con sus avisos.
const CLAVE_PLEGADA = 'consola.casos.vistasPlegadas';

export function VistasNav() {
  const { puede } = useSession();
  const esAdmin = puede('casos.sinGrupo');
  const vistas = useVistasVisibles();
  const conteos = useConteosVistas();
  const huerfanos = useConteoHuerfanos();
  const sinCerrar = useCasosSinCerrar();
  const dia = useMiDia(sinCerrar.data);
  const [sp] = useSearchParams();
  const { pathname } = useLocation();
  const [plegada, setPlegada] = useState(() => readJSON<boolean>(CLAVE_PLEGADA, false));
  const plegar = (si: boolean) => (setPlegada(si), writeJSON(CLAVE_PLEGADA, si));
  if (!pathname.startsWith('/bandeja/casos')) return null;
  const actual = sp.get('caso') ? '' : (sp.get('vista') ?? 'dia');
  // Al cambiar de vista se conservan los filtros de la barra.
  const filtros = ['q', 'grupo', 'estado', 'quien'].filter((k) => sp.get(k)).map((k) => `&${k}=${encodeURIComponent(sp.get(k)!)}`).join('');
  const enlace = (v: string) => `/bandeja/casos?vista=${v}${filtros}`;
  const nDia = dia.respondieron.length + dia.seguimientos.length + dia.nuevosMios.length;
  // Plegada (D61): una sola linea con la vista actual, para que "Abiertos ahora" tenga espacio. Se recuerda.
  // Con un caso abierto se muestra la vista de la que se vino (?vista=), no "un caso".
  const vistaVista = actual || (sp.get('vista') ?? 'dia');
  const nombreActual =
    vistaVista === 'dia' ? 'Mi día' : vistaVista === 'todos' ? 'Todos los casos' : vistaVista === 'huerfanos' ? 'Sin grupo del CRM' : (vistas.find((v) => v.id === vistaVista)?.nombre ?? 'Mi día');
  const nActual = vistaVista === 'dia' ? nDia : vistaVista === 'huerfanos' ? (huerfanos.data ?? 0) : (conteos.data?.get(vistaVista) ?? 0);
  if (plegada)
    return (
      <nav className="pm-sub pm-sub-plegada" aria-label="Vistas de casos">
        <button type="button" className="pm-plegar" onClick={() => plegar(false)} aria-expanded={false} title="Mostrar las vistas de casos">
          <Icon name="chevRight" size={13} />
          <span className="t">{nombreActual}</span>
          {nActual ? <span className="cnt">{nActual > 999 ? '999+' : nActual}</span> : null}
        </button>
      </nav>
    );
  return (
    <nav className="pm-sub" aria-label="Vistas de casos">
      <button type="button" className="pm-plegar pm-plegar-x" onClick={() => plegar(true)} aria-expanded title="Plegar las vistas (más espacio para Abiertos ahora)">
        <Icon name="chevUp" size={13} />
        <span className="t">Plegar vistas</span>
      </button>
      <Link to={enlace('dia')} aria-current={actual === 'dia' ? 'page' : undefined}>
        <span className="t">Mi día</span>
        {nDia ? <span className="cnt">{nDia}</span> : null}
      </Link>
      <Link to={enlace('todos')} aria-current={actual === 'todos' ? 'page' : undefined}>
        <span className="t">Todos los casos</span>
      </Link>
      {esAdmin && huerfanos.data ? (
        <Link to={enlace('huerfanos')} aria-current={actual === 'huerfanos' ? 'page' : undefined} title="Casos de grupos que no existen o están inactivos en el CRM">
          <span className="t">Sin grupo del CRM</span>
          <span className="cnt">{huerfanos.data > 999 ? '999+' : huerfanos.data}</span>
        </Link>
      ) : null}
      {vistas.length ? <span className="pm-sep">Vistas guardadas</span> : null}
      {vistas.map((v) => {
        const n = conteos.data?.get(v.id);
        return (
          <Link key={v.id} to={enlace(v.id)} aria-current={actual === v.id ? 'page' : undefined}>
            <span className="t">{v.nombre}</span>
            {n ? <span className="cnt">{n > 999 ? '999+' : n}</span> : null}
          </Link>
        );
      })}
    </nav>
  );
}

// "Abiertos ahora": los casos que abriste, fijos bajo la Bandeja, con su aviso (late si el cliente respondio o espera
// respuesta). Se ven en todas las secciones de la Bandeja; al tocar uno se abre en Casos.
export function AbiertosAhora() {
  const { yo } = useSession();
  const ids = usePestanas(yo.miembroId);
  const sinCerrar = useCasosSinCerrar();
  const [sp] = useSearchParams();
  const ir = useNavigate();
  const { pathname } = useLocation();
  const porId = new Map((sinCerrar.data ?? []).map((c) => [c.id, c]));
  // Los que ya no estan entre los sin cerrar de tus grupos (resueltos, cerrados u otro grupo) se traen aparte.
  const faltan = ids.filter((id) => !porId.has(id));
  const sueltos = useQuery({
    queryKey: ['caso', 'pestanas', faltan.join(',')],
    queryFn: async () => (await gqlTodos<RawCaso>('casos', CAMPOS_LISTA, { filtro: { id: { in: faltan } }, max: faltan.length })).map(aCaso),
    enabled: faltan.length > 0 && !!sinCerrar.data,
    staleTime: 60_000,
  });
  for (const c of sueltos.data ?? []) porId.set(c.id, c);
  if (!ids.length || !pathname.startsWith('/bandeja')) return null;
  const enCasos = pathname.startsWith('/bandeja/casos');
  const abierto = enCasos ? sp.get('caso') : null;
  const abrir = (id: string) => {
    const n = new URLSearchParams(enCasos ? sp : undefined);
    n.set('caso', id);
    ir(`/bandeja/casos?${n.toString()}`);
  };
  return (
    <section className="pestanas" aria-label="Casos abiertos ahora">
      <div className="pest-h">
        Abiertos ahora <span>{ids.length}</span>
      </div>
      {ids.map((id) => {
        const c = porId.get(id);
        const s = c ? senalDe(c) : null;
        return (
          <div key={id} className={`pest ${s ? `s-${s}` : ''}`} aria-current={abierto === id ? 'true' : undefined}>
            <button type="button" className="pest-abrir" onClick={() => abrir(id)} title={c ? `#${c.numero} · ${c.asunto} · ${c.empresa?.nombre || c.persona?.nombre || c.correoCliente || ''}${s ? ` · ${textoSenal(c, s)}` : ''}` : 'Caso'}>
              <span className="punto" aria-hidden="true" />
              <span className="t1">{c ? c.asunto || `#${c.numero}` : 'Cargando…'}</span>
              <span className="t2">
                {c ? c.empresa?.nombre || c.persona?.nombre || c.correoCliente || `#${c.numero}` : ''}
                {c && s ? <span className="sr-only"> · {textoSenal(c, s)}</span> : null}
              </span>
            </button>
            <button type="button" className="pest-cerrar" onClick={() => quitarCaso(yo.miembroId, id)} aria-label="Quitar de abiertos ahora">
              <Icon name="x" />
            </button>
          </div>
        );
      })}
    </section>
  );
}
